import { Injectable, inject } from '@angular/core';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { env } from '../env.generated';

// Importar configuración por defecto
import defaultConfig from '../mission-configs/default.config.json';

/**
 * Configuración base de una misión
 */
export interface MissionConfig {
    missionId: string;
    missionName: string;
    appType: 'panel' | 'iglesia';
    functionsRegion: string;
}

/**
 * Configuración de items de offerings (ingresos/egresos)
 */
export interface OfferingItem {
    id: string;
    nombre: string;
    tipo: 'ingreso' | 'egreso';
    activo: boolean;
    orden: number;
    icono?: string;
}

/**
 * Configuración de campos de reportes
 */
export interface ReportField {
    id: string;
    nombre: string;
    tipo: 'text' | 'number' | 'boolean' | 'date';
    requerido: boolean;
    orden: number;
    visible: boolean;
}

/**
 * Personalizaciones específicas por misión
 */
export interface MissionCustomizations {
    // Items de offerings
    offeringItems?: {
        ingresos: OfferingItem[];
        egresos: OfferingItem[];
    };

    // Campos de reportes
    reportFields?: {
        [reportType: string]: ReportField[];
    };

    // Labels y textos personalizados
    labels?: {
        [key: string]: string;
    };

    // Configuraciones adicionales
    settings?: {
        [key: string]: any;
    };
}

/**
 * País disponible en la misión
 */
export interface Country {
    id: string;
    nombre: string;
    codigo?: string; // Opcional
    activo: boolean;
    orden: number;
    numberOfZones?: number; // Cantidad de zonas autogeneradas
}

/**
 * Zona geográfica dentro de un país
 */
export interface Zone {
    id: string;
    nombre: string;
    countryId: string;
    activo: boolean;
    orden: number;
}

/**
 * Configuración completa de la misión (para Firestore)
 */
export interface MissionSettings {
    offeringItems?: {
        ingresos: OfferingItem[];
        egresos: OfferingItem[];
    };
    reportFields?: {
        [reportType: string]: ReportField[];
    };
    countries?: Country[];
    // zones?: Zone[]; // Deprecado: Se generan dinámicamente desde countries
    labels?: {
        [key: string]: string;
    };
    settings?: {
        [key: string]: any;
    };
    lastModified?: Date;
    modifiedBy?: string;
}



/**
 * Servicio centralizado para gestionar configuraciones multi-misión
 */
@Injectable({
    providedIn: 'root'
})
export class MissionConfigService {

    private currentMission: MissionConfig;
    private customizations: MissionCustomizations | null = null;
    private configSource: 'firestore' | 'json' = 'json';
    private firestore = inject(Firestore);
    private loadingPromise: Promise<void>;

    constructor() {
        // Cargar configuración de la misión actual desde env.generated
        this.currentMission = {
            missionId: env.NG_APP_MISSION_ID,
            missionName: env.NG_APP_MISSION_NAME,
            appType: env.NG_APP_APP_TYPE as 'panel' | 'iglesia',
            functionsRegion: env.NG_APP_FUNCTIONS_REGION
        };

        // Cargar personalizaciones de la misión
        this.loadingPromise = this.loadCustomizations();
    }

    /**
     * Asegura que la configuración esté cargada
     */
    async ensureInitialized(): Promise<void> {
        return this.loadingPromise;
    }

    /**
     * Obtiene la configuración de la misión actual
     */
    getCurrentMission(): MissionConfig {
        return { ...this.currentMission };
    }

    /**
     * Obtiene el ID de la misión actual
     */
    getMissionId(): string {
        return this.currentMission.missionId;
    }

    /**
     * Obtiene el nombre de la misión actual
     */
    getMissionName(): string {
        return this.currentMission.missionName;
    }

    /**
     * Obtiene el tipo de aplicación
     */
    getAppType(): 'panel' | 'iglesia' {
        return this.currentMission.appType;
    }

    /**
     * Obtiene la región de Cloud Functions
     */
    getFunctionsRegion(): string {
        return this.currentMission.functionsRegion;
    }

    /**
     * Obtiene el origen de la configuración actual
     */
    getConfigSource(): 'firestore' | 'json' {
        return this.configSource;
    }

    /**
     * Verifica si se está usando configuración personalizada de Firestore
     */
    isUsingCustomConfig(): boolean {
        return this.configSource === 'firestore';
    }

    /**
     * Obtiene la lista de países disponibles
     */
    getCountries(): Country[] {
        const settings = this.customizations as MissionSettings;
        return settings?.countries || [];
    }

    /**
     * Obtiene la lista de zonas disponibles (Generadas dinámicamente)
     */
    getZones(countryId?: string): Zone[] {
        const countries = this.getCountries();
        let zones: Zone[] = [];

        countries.forEach(country => {
            const count = country.numberOfZones || 0;
            if (count > 0) {
                for (let i = 1; i <= count; i++) {
                    zones.push({
                        id: `${country.id}-zona-${i}`,
                        nombre: `Zona ${i}`,
                        countryId: country.id,
                        activo: true,
                        orden: i
                    });
                }
            }
        });

        if (countryId) {
            return zones.filter(z => z.countryId === countryId);
        }

        return zones;
    }

    /**
     * Obtiene todas las personalizaciones de la misión
     */
    getCustomizations(): MissionCustomizations | null {
        return this.customizations;
    }

    /**
     * Obtiene una personalización específica por clave
     */
    getCustomization<T>(key: keyof MissionCustomizations): T | undefined {
        if (!this.customizations) return undefined;
        return this.customizations[key] as T;
    }

    /**
     * Obtiene items de offerings (ingresos o egresos)
     */
    getOfferingItems(tipo: 'ingresos' | 'egresos'): OfferingItem[] {
        const offerings = this.getCustomization<MissionCustomizations['offeringItems']>('offeringItems');
        if (!offerings) return [];
        return offerings[tipo] || [];
    }

    /**
     * Obtiene campos de un tipo de reporte específico
     */
    getReportFields(reportType: string): ReportField[] {
        const reportFields = this.getCustomization<MissionCustomizations['reportFields']>('reportFields');
        if (!reportFields) return [];
        return reportFields[reportType] || [];
    }

    /**
     * Obtiene un label personalizado
     */
    getLabel(key: string, defaultValue?: string): string {
        const labels = this.getCustomization<MissionCustomizations['labels']>('labels');
        if (!labels) return defaultValue || key;
        return labels[key] || defaultValue || key;
    }

    /**
     * Obtiene una configuración específica
     */
    getSetting<T>(key: string, defaultValue?: T): T | undefined {
        const settings = this.getCustomization<MissionCustomizations['settings']>('settings');
        if (!settings) return defaultValue;
        return settings[key] !== undefined ? settings[key] : defaultValue;
    }

    /**
     * Verifica si estamos en una misión específica
     */
    isMission(missionId: string): boolean {
        return this.currentMission.missionId === missionId;
    }

    /**
     * Verifica si estamos en el panel administrativo
     */
    isPanel(): boolean {
        return this.currentMission.appType === 'panel';
    }

    /**
     * Verifica si estamos en la aplicación de iglesias
     */
    isIglesia(): boolean {
        return this.currentMission.appType === 'iglesia';
    }

    /**
     * Carga las personalizaciones de la misión actual
     * Intenta primero desde Firestore, luego fallback a JSON
     */
    private async loadCustomizations(): Promise<void> {
        try {
            // 1. Intentar cargar desde Firestore
            const firestoreConfig = await this.loadFromFirestore();

            if (firestoreConfig) {
                this.customizations = firestoreConfig;
                this.configSource = 'firestore';
                return;
            }
        } catch (error) {
        }

        // 2. Fallback a configuración JSON predefinida
        // Usar siempre la configuración por defecto si no hay nada en Firestore
        this.customizations = defaultConfig as MissionCustomizations;
        this.configSource = 'json';
    }

    /**
     * Carga la configuración desde Firestore
     */
    private async loadFromFirestore(): Promise<MissionSettings | null> {
        try {
            const docRef = doc(this.firestore, `missionSettings/${this.currentMission.missionId}`);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                return docSnap.data() as MissionSettings;
            }

            return null;
        } catch (error) {
            return null;
        }
    }

    /**
     * Obtiene la configuración predefinida (JSON)
     */
    getDefaultConfig(): MissionCustomizations | null {
        return defaultConfig as MissionCustomizations;
    }
}
